import React, {useEffect, useState} from "react";
import {getShopId} from "../lib/getShopId";
import {useCustomerContext} from "../customer/CustomerContextProvider/CustomerContextProvider";
import {INewsletter} from "@connox/typescript-library";
import Checkbox from "../lib/form/Checkbox/Checkbox";
import Select from "../lib/form/Select/Select";
import {useTranslation} from "react-i18next";
import Spinner from "../utils/loader/Spinner";
import {useForm, Controller} from "react-hook-form";
import styles from "./NewsLetterOptionsForm.module.scss";
import {Button} from "../lib/form/Button/Button";
import {useMediaQuery} from "react-responsive";

type CustomerData = {gender: "F" | "M"; firstname: string; lastname: string; birthday: string};
const customSortArray = [100014, 100000, 100008, 100007, 100009, 100015, 100018];
export const deletedNewsletterTypes = [100010, 100012, 100013, 100016, 100017, 100020, 100022];

const NewsLetterOptionsForm: React.FC = () => {
  const {customer} = useCustomerContext();
  const {t} = useTranslation("customerAccount");
  const [nlOptionsState, setNlOptionsState] = useState<INewsletter[]>([]);
  const [customerData, setCustomerData] = useState<CustomerData>();
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isBirthdayRequired, setIsBirthdayRequired] = useState(false);
  const isMobile = useMediaQuery({maxWidth: 480});
  const [successState, setSuccessState] = useState<{
    updateSubscribe?: boolean;
    firstSubscribe?: boolean;
  }>({firstSubscribe: true});

  useEffect(() => {
    void customer.getNewsletterOptions(getShopId()).then((data) => {
      setNlOptionsState(data.filter((option) => !deletedNewsletterTypes.includes(option.newsletterTypeId)));
    });
  }, []);

  useEffect(() => {
    setButtonDisabled(nlOptionsState.every((option) => !option.isSubscribed));
  }, [nlOptionsState]);

  const submitData = async () => {
    if (nlOptionsState.find((option) => option.newsletterTypeId === 100009)?.isSubscribed && !customerData?.birthday) {
      setIsBirthdayRequired(true);

      return;
    }

    setIsBirthdayRequired(false);
    setIsLoading(true);
    await customer.putProperties(getShopId(), {
      firstName: customerData?.firstname,
      lastName: customerData?.lastname,
      birthday: customerData?.birthday,
      gender: customerData?.gender || null,
    });
    await customer.setNewsletterOptions(getShopId(), nlOptionsState);
    setIsLoading(false);
    setSuccessState({updateSubscribe: true});
    setButtonDisabled(true);
    window.scrollTo({top: 0, left: 0, behavior: "smooth"});
  };

  const BrandOfTheMonthButton = () => {
    return (
      <div className={styles.brandOfTheMonthButtonWrapper}>
        <div className={styles.title}>{"Marke des Monats"}</div>
        <Button as={"link"} href={"https://www.connox.de/marke-des-monats.html"}>
          {"Jetzt alle Aktionen ansehen"}
        </Button>
      </div>
    );
  };

  const isGermanShop = getShopId() === (100001 || 100009 || 100017);

  return (
    <div>
      {successState.firstSubscribe && (
        <SuccessMessageBlock>
          {t("Sie haben sich erfolgreich zu unserem Newsletter angemeldet. Vielen Dank!")}
          <br />
          {t("Ihren 12 %-Willkommensrabatt erhalten Sie in einer separaten E-Mail.")}
        </SuccessMessageBlock>
      )}
      {successState.updateSubscribe && (
        <SuccessMessageBlock>{t("Ihre Änderungen wurden erfolgreich gespeichert!")}</SuccessMessageBlock>
      )}

      <div className={styles.formsWrapper}>
        <div>
          <PersonalDataForm
            onChange={(data) => {
              setButtonDisabled(false);
              setCustomerData(data);
            }}
            isBirthdayRequired={isBirthdayRequired}
          />
          {isGermanShop && !isMobile && <BrandOfTheMonthButton />}
        </div>
        <OptionsForm
          nlOptions={nlOptionsState}
          onChange={(options) => {
            setNlOptionsState(options);
            setButtonDisabled(false);
          }}
        />
      </div>
      <div className={styles.buttonWrapper}>
        <button
          disabled={buttonDisabled}
          className={styles.submitButton}
          onClick={submitData}
        >
          {isLoading ? <Spinner /> : <span>{t("Newslettereinstellungen speichern")}</span>}
        </button>
      </div>
      {isGermanShop && isMobile && <BrandOfTheMonthButton />}
    </div>
  );
};

const PersonalDataForm: React.FC<{onChange: (data: CustomerData) => void; isBirthdayRequired: boolean}> = ({
  onChange,
  isBirthdayRequired,
}) => {
  const {t} = useTranslation("customerAccount");
  const {register, watch, control} = useForm<CustomerData>({});

  return (
    <div className={styles.personalDataFormWrapper}>
      <div className={styles.title}>{t("Wie dürfen wir Sie ansprechen?")}</div>
      <form
        onChange={() => {
          onChange(watch());
        }}
      >
        <div className={styles.inputWrapper}>
          <div className={styles.labelText}>{t("Anrede")}</div>
          <Controller
            control={control}
            render={({field}) => (
              <Select
                styles={{wrapper: styles.select}}
                onChange={(value) => {
                  field.onChange(value);
                  onChange(watch());
                }}
                placeHolder={t("Bitte auswählen")}
                options={[
                  {value: "F", text: t("Frau")},
                  {value: "M", text: t("Herr")},
                ]}
              />
            )}
            name={"gender"}
          />
        </div>

        <div className={styles.inputWrapper}>
          <div className={styles.labelText}>{t("Vorname")}</div>
          <input className={styles.input} {...register("firstname")} />
        </div>

        <div className={styles.inputWrapper}>
          <div className={styles.labelText}>{t("Nachname")}</div>
          <input className={styles.input} {...register("lastname")} />
        </div>

        <div className={styles.inputWrapper}>
          <div className={styles.labelText}>{t("Geburtstag")}</div>
          <input type={"date"} className={styles.input} {...register("birthday")} />
          {isBirthdayRequired && !watch().birthday && (
            <span className={styles.error}>
              {t("Bitte geben Sie Ihren Geburtstag an, damit wir Ihnen den Geburtstagsnewsletter zuschicken können.")}
            </span>
          )}
        </div>
      </form>
    </div>
  );
};

export const SuccessMessageBlock: React.FC = ({children}: {children}) => {
  return (
    <div className={styles.successMessageWrapper}>
      <div>{children}</div>
    </div>
  );
};

export const OptionsForm: React.FC<{
  nlOptions: INewsletter[];
  onChange: (options: INewsletter[]) => void;
  showBirthdayInput?: boolean;
  onChangeBirthday?: (birthday: string) => void;
  isBirthdayRequired?: boolean;
}> = ({nlOptions, onChange, showBirthdayInput = false, onChangeBirthday, isBirthdayRequired = false}) => {
  const [isAllChecked, setIsAllChecked] = useState(false);
  const {t} = useTranslation("customerAccount");
  const {customer} = useCustomerContext();
  const [birthday, setBirthday] = useState<string | null>();

  useEffect(() => {
    void customer.retrieveProperties(getShopId()).then((data) => {
      setBirthday(data?.birthday);
      if (onChangeBirthday) {
        onChangeBirthday(data?.birthday ?? "");
      }
    });
  }, []);

  useEffect(() => {
    setIsAllChecked(nlOptions.every((option) => option.isSubscribed));
  }, [nlOptions]);

  return (
    <div className={styles.optionsFormWrapper}>
      <div className={styles.title}>{t("Zu welchen Themen möchten Sie Newsletter abonnieren?")}</div>
      <div className={styles.checkboxWrapper}>
        <Checkbox
          className={styles.checkbox}
          checked={isAllChecked}
          color={"#0caa00"}
          onChange={(checked) => {
            setIsAllChecked(checked);
            const options = [...nlOptions];
            options.forEach((option, index) => {
              options[index] = {...option, isSubscribed: checked};
            });
            onChange(options);
          }}
        >
          <p className={styles.checkboxTitle}>{t("Alle Themen")}</p>
        </Checkbox>
      </div>
      {customSortArray.map((id) => {
        const option = nlOptions.find((optionToFind) => optionToFind.newsletterTypeId === id);
        const index = nlOptions.findIndex((optionToFind) => optionToFind.newsletterTypeId === id);
        if (!option) {
          return null;
        }

        return (
          <div key={option.newsletterTypeId} className={styles.checkboxWrapper}>
            <Checkbox
              className={styles.checkbox}
              checked={option.isSubscribed}
              onChange={(checked) => {
                const options = [...nlOptions];
                options[index] = {...options[index], isSubscribed: checked};
                onChange(options);
              }}
              color={"#0caa00"}
            >
              <div className={styles.checkboxContent}>
                <p className={styles.checkboxTitle}>{option.newsletterTypeName}</p>
                <p className={styles.checkboxSubtitle}>{option.newsletterTypeDescription}</p>
                {option.newsletterTypeId === 100009 && showBirthdayInput && (
                  <div className={styles.inputWrapper}>
                    <div className={styles.labelText}>{t("Geburtstag")}</div>
                    <input
                      type={"date"}
                      className={styles.input}
                      value={birthday || ""}
                      onChange={(e) => {
                        setBirthday(e.target.value);
                        if (onChangeBirthday) {
                          onChangeBirthday(e.target.value);
                        }
                      }}
                    />
                    {!birthday && isBirthdayRequired && (
                      <span className={styles.error}>
                        {t(
                          "Bitte geben Sie Ihren Geburtstag an, damit wir Ihnen den Geburtstagsnewsletter zuschicken können.",
                        )}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </Checkbox>
          </div>
        );
      })}
    </div>
  );
};

export default NewsLetterOptionsForm;
